import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { X, Save, FileText, AlertCircle } from 'lucide-react';

export default function ConvertToTemplateModal({ isOpen, onClose, workout, onSuccess }) {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();

  const [formData, setFormData] = useState({
    template_name: '',
    template_description: '',
    difficulty_level: 'intermediate',
    target_goal: 'general',
    estimated_duration: 45,
    include_notes: true,
    group_id: null
  });

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Carica gruppi disponibili
  useEffect(() => {
    if (isOpen) {
      loadGroups();
    }
  }, [isOpen]);

  // Prefill nome template con nome scheda (workout da WorkoutsTab ha .name, da API può avere .nome)
  useEffect(() => {
    if (isOpen && workout) {
      const workoutName = workout.name || workout.nome || '';
      setFormData(prev => ({
        ...prev,
        template_name: workoutName
      }));
    }
  }, [isOpen, workout]);

  const loadGroups = async () => {
    setLoadingGroups(true);
    try {
      const response = await api.get('/gym_template_groups.php?action=list');
      setGroups(response.data || []);
    } catch (error) {
      console.error('Errore caricamento gruppi:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : (name === 'group_id' && value === '' ? null : value)
    }));
    
    // Rimuovi errore di validazione
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.template_name.trim()) {
      errors.template_name = 'Nome template richiesto';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = {
        workout_id: workout.id,
        template_name: formData.template_name.trim(),
        template_description: formData.template_description && formData.template_description.trim() !== '' 
          ? formData.template_description.trim() 
          : null,
        difficulty_level: formData.difficulty_level,
        target_goal: formData.target_goal,
        estimated_duration: parseInt(formData.estimated_duration) || 45,
        include_notes: formData.include_notes,
        group_id: formData.group_id ? parseInt(formData.group_id) : null
      };

      const response = await api.post('/gym_workout_templates.php?action=create_from_workout', data);

      if (response.data.success) {
        toast.success('Template creato con successo!');
        onSuccess(response.data.template_id);
        onClose();
      } else {
        toast.error(response.data.error || 'Errore nella creazione del template');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Errore durante la conversione');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !workout) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
          <div className="flex min-w-0 items-center gap-3 pr-2">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-white/10">
              <FileText className="h-5 w-5 text-blue-600 dark:text-gray-300" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-gray-900 dark:font-open-sans dark:text-white">
                Salva su Template
              </h2>
              <p className="text-sm text-gray-600 dark:text-white/75">
                Crea un template riutilizzabile da questa scheda
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-gray-600 transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-white p-6 dark:bg-gray-900">
          <div className="space-y-5">
            {/* Info scheda origine */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-white/10 dark:bg-gray-900">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
                    Stai convertendo la scheda "{workout.name || workout.nome || 'Senza nome'}"
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                    La scheda del cliente rimarrà invariata. Verrà creata una copia come template.
                  </p>
                </div>
              </div>
            </div>

            {/* Nome Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nome Template *
              </label>
              <input
                type="text"
                name="template_name"
                value={formData.template_name}
                onChange={handleChange}
                className={`w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:text-white dark:focus:ring-indigo-500/30 ${
                  validationErrors.template_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-700 dark:bg-gray-800'
                }`}
                placeholder="Es: Full Body A - Forza"
              />
              {validationErrors.template_name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{validationErrors.template_name}</p>
              )}
            </div>

            {/* Descrizione */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descrizione (opzionale)
              </label>
              <textarea
                name="template_description"
                value={formData.template_description}
                onChange={handleChange}
                rows="3"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
                placeholder="Es: Scheda 3x settimana per aumento forza"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Difficoltà */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Difficoltà
                </label>
                <select
                  name="difficulty_level"
                  value={formData.difficulty_level}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
                >
                  <option value="beginner">Principiante</option>
                  <option value="intermediate">Intermedio</option>
                  <option value="advanced">Avanzato</option>
                  <option value="expert">Esperto</option>
                </select>
              </div>

              {/* Obiettivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Obiettivo
                </label>
                <select
                  name="target_goal"
                  value={formData.target_goal}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
                >
                  <option value="general">Generale</option>
                  <option value="strength">Forza</option>
                  <option value="hypertrophy">Ipertrofia</option>
                  <option value="endurance">Resistenza</option>
                  <option value="flexibility">Flessibilità</option>
                </select>
              </div>
            </div>

            {/* Durata stimata */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Durata Stimata (minuti)
              </label>
              <input
                type="number"
                name="estimated_duration"
                value={formData.estimated_duration}
                onChange={handleChange}
                min="15"
                max="180"
                step="5"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
              />
            </div>

            {/* Gruppo (opzionale) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Gruppo (opzionale)
              </label>
              <select
                name="group_id"
                value={formData.group_id || ''}
                onChange={handleChange}
                disabled={loadingGroups}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30"
              >
                <option value="">Nessun gruppo</option>
                {groups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.icon ? `${group.icon} ` : ''}{group.name}
                  </option>
                ))}
              </select>
              {loadingGroups && (
                <p className="mt-1 text-xs text-gray-500">Caricamento gruppi...</p>
              )}
            </div>

            {/* Opzioni */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="include_notes"
                  checked={formData.include_notes}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Includi note trainer negli esercizi
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-white/10 dark:bg-gray-800">
          <div className="flex w-full items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-lg bg-white px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-white dark:hover:brightness-110"
            >
              Annulla
            </button>
            
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center rounded-lg bg-blue-600 px-6 py-2 text-white transition-colors hover:bg-blue-700 disabled:bg-gray-400 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110 dark:disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creazione...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Salva su Template
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
